import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthenticationToken } from './authentication-token';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { jwtDecode } from "jwt-decode";
import { ActivatedRoute, Router } from '@angular/router';
import { UserApiService } from '@core/api/services/user-api.service';
import { UserProfileApiService } from '@core/api/services/user-profile-api.service';
import { User, UserAdapter } from '@core/models/user/user.model';
import { SamlService } from '@modules/saml/services/saml.service';
import { OrganisationService } from '@core/services/organisation.service';



@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authenticated: BehaviorSubject<boolean>;

  constructor(
    private httpClient: HttpClient,
    private tokenStorage: TokenStorageService,
    private userService:UserApiService,
    private userProfileService:UserProfileApiService,
    private userAdapter:UserAdapter,
    private route: ActivatedRoute,
    private router:Router,
    private samlService:SamlService,
    private injector:Injector
  ) {
    this.authenticated = new BehaviorSubject<boolean>(this.tokenStorage.hasToken());
  }

  isAuthenticated(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  getAuthToken(): string {
    return this.tokenStorage.getToken()??"";
  }

  getToken():any{
    return jwtDecode(this.getAuthToken());
  }

  get redirectUrl():string{
    const redirectUrl = localStorage.getItem('redirectUrl_sl');
    return redirectUrl??'/';
  }

  get User():User{
    const body =  JSON.parse(localStorage.getItem("user_sl") as string);
    if(!body){
      return new User("");
    }
      return new User(
        body.id,
        body.profileId,
        body.createdAt,
        body.firstName,
        body.lastName,
        body.email,
        body.communicationEmail,
        body.language,
        body.blacklisted,
        body.image,
        body.gender,
        body.dateOfBirth,
        body.referral,
        body.referralUserId,
        body.referralUsed,
        body.referralDenied,
        body.emailMfaEnabled,
        body.icalUrl,
        body.lastLogin ? new Date(body.lastLogin) : null);
  }

  verifyMfaCode(authCode: string, trusted:boolean = false): Observable<boolean> {
    return this.httpClient
      .post<any>(`${environment.meWrapperApiUrl}/auth/authentication_token/2fa`, {
        data: { authCode },
        _trusted: trusted,
      }, {
        observe: 'response',
        withCredentials: true,
      })
      .pipe(
        mergeMap((resp: HttpResponse<any>) => {
          if (resp.status === 200 && resp.body?.token) {
            return this.loginOnRegister(resp.body);
          }
          return of(false);
        }),
        map((user) => !!user)
      );
  }

  login(emailAddress: string, password: string, branchId:string|null = null): Observable<boolean> {
    const body = {emailAddress, password, type:"platform", "branchId": branchId};

    return this.httpClient.post<any>(`${environment.meWrapperApiUrl}/auth/authentication_token`, body, {
      observe: 'response',
      withCredentials: true,
    }).pipe(
      //catchError(this.handleLoginError()),
      switchMap((value:any) =>
        {
          if(value.status === 200 && value.body?.token){
            return this.loginOnRegister(value.body);
          }
          if(value){
            return this.loginOnRegister(value);
          }
          return of(false);
        }),
      map((user) => {
        const redirectUrl = this.route.snapshot.queryParams.continue;
        if (redirectUrl) {
          this.setRedirectUrl(redirectUrl);
        }

        if (user) {
          // Fresh login: drop the onboarding-skip flag so users who still have zero organisations land
          // back in the onboarding flow, and drop the welcome-overlay flag so the overlay is
          // re-evaluated from the user's previous login (shown on a first login or after a 3+ month gap)
          // instead of staying dismissed for good.
          // Literals, not the ONBOARDING_*_FLAG constants: those live in the guard, which imports this
          // service.
          localStorage.removeItem('onboarding_skipped_sl');
          localStorage.removeItem('onboarding_welcome_seen_sl');
        }

        return !!user;
      }),

    );
  }

  updateUser(item:any){
    if(this.getToken().isImpersonating){
      item.emailMfaEnabled = true;
    }
    localStorage.setItem("user_sl",JSON.stringify(item));
  }

  setRedirectUrl(url:string){
    localStorage.setItem('redirectUrl_sl',decodeURIComponent(url));
  }

  navigateToRedirectUrl(){
    const redirectUrl = this.redirectUrl;
    localStorage.removeItem('redirectUrl_sl');
    this.router.navigateByUrl(redirectUrl);
  }

  handleSAMLRequest(samlResponse:string, relayState:string) {
    //do an API call and adapt the response to a variable so I can extract the redirect URL
    return this.samlService.getRedirectUrl(samlResponse, relayState).subscribe((x:any) => {
      window.location.href= x.redirect;
    })
  }

  logout() {
    this.removeAuthenticationToken();
    localStorage.removeItem('admin_block');
    // Lazy-resolve OrganisationService — declaring it as a constructor dep cycles
    // through HTTP_INTERCEPTORS (AuthInterceptor → AuthService → OrganisationService → HttpClient).
    const organisationService = this.injector.get(OrganisationService);
    organisationService.clear();
    organisationService.clearOrgList();
  }

  get IsLoggedIn(){
    return this.tokenStorage.hasToken() && this.User.id;
  }

  ssoLogin(authToken: AuthenticationToken) {
    this.setAuthenticationToken(authToken);
    const userCall = this.userService.get(this.getToken().id);
    const profileCall = this.userProfileService.get(this.getToken().profileId);
    return forkJoin({user:userCall, profile:profileCall}).pipe(map((x:any) => { const user = this.userAdapter.adapt(x); localStorage.setItem("user_sl",JSON.stringify(user));this.authenticated.next(true); return user}));
  }

  loginOnRegister(authToken: AuthenticationToken) {
    this.setAuthenticationToken(authToken);
    const userCall = this.userService.get(this.getToken().id);
    const profileCall = this.userProfileService.get(this.getToken().profileId);
    return forkJoin({user:userCall, profile:profileCall}).pipe(tap((x:any) => {
      const u = this.userAdapter.adapt(x);
      if(this.getToken().isImpersonating){
        u.emailMfaEnabled = true;
      } 
      localStorage.setItem("user_sl",JSON.stringify(u));
      this.authenticated.next(true);
    }));
  }

  updateLanguage(profileId:string,language:string){
      return this.userProfileService.update(profileId,{language: 'languages/'+ language});
  }

  
  setAuthenticationToken(authToken: AuthenticationToken): void {
    this.tokenStorage.setToken(authToken.token);
  }

  removeAuthenticationToken(): void {
    this.tokenStorage.removeToken();
    this.authenticated.next(false);
  }
}
